import { useMemo, useRef, useState } from 'react';
import {
  IMMUTABLE_OBJECT_ATTRIBUTES,
  OBJECT_ID_KEY,
  RESERVED_OBJECT_ATTRIBUTE_NAMES,
} from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';

export type AttributeRow = {
  /** Stable row identity across key/value edits. */
  id: number;
  key: string;
  value: string;
  /** null for rows created in this editing session ("new attribute" rows). */
  originalKey: string | null;
  originalValue: string | null;
  serverError: string | null;
};

export type AttributeRowErrors = { key?: string; value?: string };

type UseObjectAttributesOptions = {
  /** Ordered `{ key: value }` pairs, e.g. from `filteredAttrsWithIdentifiers`. */
  initialAttrs: ReadonlyArray<Record<string, string>>;
  isNew: boolean;
};

const AUTO_GENERATED_ATTRIBUTES: readonly string[] = IMMUTABLE_OBJECT_ATTRIBUTES.filter((key) => key !== OBJECT_ID_KEY);

// The backend's strutil.HasInvalidCharacters equivalent: a replacement
// character means the name arrived with broken encoding.
function hasInvalidCharacters(value: string): boolean {
  return value.includes('�');
}

export function attributeRowErrors(
  row: AttributeRow,
  isNew: boolean,
  duplicateKeys?: ReadonlySet<string>,
): AttributeRowErrors {
  const errors: AttributeRowErrors = {};
  const key = row.key.trim();
  const isKeyChanged = row.key !== row.originalKey;

  if (row.key === OBJECT_ID_KEY && isEmptyString(row.value)) {
    errors.value = "Attribute value can't be blank";
  }
  if (isEmptyString(row.key) && !isEmptyString(row.value)) {
    errors.key = "Attribute name can't be blank";
  }
  if (hasInvalidCharacters(row.key)) {
    errors.key = 'Attribute name contains invalid characters';
  }
  if (isNew && isKeyChanged && AUTO_GENERATED_ATTRIBUTES.includes(key)) {
    errors.key = `${key} is auto-generated in this workspace`;
  } else if (isKeyChanged && RESERVED_OBJECT_ATTRIBUTE_NAMES.includes(key)) {
    errors.key = 'Attribute name is already used';
  }
  if (errors.key == null && duplicateKeys?.has(row.key) === true && !isEmptyString(row.key)) {
    errors.key = 'Attribute name is already used';
  }
  if (row.serverError != null) {
    errors.value = row.serverError;
  }

  return errors;
}

function duplicateKeySet(rows: readonly AttributeRow[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of rows) {
    if (row.key === '') continue;
    if (seen.has(row.key)) duplicates.add(row.key);
    seen.add(row.key);
  }
  return duplicates;
}

/**
 * The hidden list after renaming a row: the new name must not stay hidden
 * (renamed away then re-added), and the original key becomes a delete on save —
 * unless another (unedited) row still holds it, as when two rows swap keys.
 */
function hiddenAttrsAfterKeyChange(
  hidden: readonly string[],
  rows: readonly AttributeRow[],
  originalKey: string | null,
  newKey: string,
): string[] {
  const next = hidden.filter((attr) => attr !== newKey);
  if (originalKey == null || originalKey === '') return next;
  const rowWithOriginalKey = rows.find((candidate) => candidate.key === originalKey);
  const originalHolderUnchanged =
    rowWithOriginalKey != null && rowWithOriginalKey.key === rowWithOriginalKey.originalKey;
  return originalHolderUnchanged ? next : [...next, originalKey];
}

function rowsFromAttrs(initialAttrs: ReadonlyArray<Record<string, string>>, nextId: () => number): AttributeRow[] {
  return initialAttrs.map((attr) => {
    const [key = ''] = Object.keys(attr);
    return {
      id: nextId(),
      key,
      value: attr[key] ?? '',
      originalKey: key === '' ? null : key,
      originalValue: key === '' ? null : (attr[key] ?? ''),
      serverError: null,
    };
  });
}

/**
 * The object attributes editing model: rows with per-row validation and
 * server errors, plus the `hiddenAttrs` list of removed/renamed keys the
 * save serializes to empty strings (the API's "delete attribute").
 */
export function useObjectAttributes({ initialAttrs, isNew }: UseObjectAttributesOptions) {
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;
  const [originalRows] = useState<AttributeRow[]>(() => rowsFromAttrs(initialAttrs, nextId));
  const [rows, setRows] = useState<AttributeRow[]>(originalRows);
  const [hiddenAttrs, setHiddenAttrs] = useState<string[]>([]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(rows.map((row) => [row.key, row.value])) !==
      JSON.stringify(originalRows.map((row) => [row.key, row.value])),
    [rows, originalRows],
  );
  const duplicateKeys = useMemo(() => duplicateKeySet(rows), [rows]);
  const errorsFor = (row: AttributeRow) => attributeRowErrors(row, isNew, duplicateKeys);
  const isInvalid = rows.some((row) => Object.keys(errorsFor(row)).length > 0);

  const create = (key = '', value = '') => {
    setRows((current) => [
      ...current,
      { id: nextId(), key, value, originalKey: null, originalValue: null, serverError: null },
    ]);
  };

  const add = (key: string) => {
    setHiddenAttrs((current) => current.filter((attr) => attr !== key));
    create(key);
  };

  const remove = (row: AttributeRow) => {
    setRows((current) => {
      const remaining = current.filter((candidate) => candidate.id !== row.id);
      // Only serialize the key as a delete when it is named and no live row
      // still holds it — removing an unnamed row (or one of two duplicates)
      // must not put a blank/surviving key into the payload.
      if (row.key !== '' && !remaining.some((candidate) => candidate.key === row.key)) {
        setHiddenAttrs((hidden) => [...hidden, row.key]);
      }
      return remaining;
    });
  };

  const setValue = (row: AttributeRow, value: string) => {
    setRows((current) =>
      current.map((candidate) => (candidate.id === row.id ? { ...candidate, value, serverError: null } : candidate)),
    );
  };

  const setKey = (row: AttributeRow, key: string) => {
    setRows((current) => {
      const updated = current.map((candidate) => (candidate.id === row.id ? { ...candidate, key } : candidate));
      setHiddenAttrs((hidden) => hiddenAttrsAfterKeyChange(hidden, updated, row.originalKey, key));
      return updated;
    });
  };

  const setServerError = (key: string, message: string) => {
    setRows((current) =>
      current.map((candidate) => (candidate.key === key ? { ...candidate, serverError: message } : candidate)),
    );
  };

  const reset = () => {
    setRows(originalRows);
    setHiddenAttrs([]);
  };

  const getModelAttributes = () => {
    const attrs: Record<string, string> = {};
    for (const row of rows) {
      if (row.key !== '') {
        attrs[row.key] = row.value;
      }
    }
    return { attrs, hiddenAttrs };
  };

  return {
    rows,
    hiddenAttrs,
    isDirty,
    isInvalid,
    isNew,
    errorsFor,
    create,
    add,
    remove,
    setKey,
    setValue,
    setServerError,
    reset,
    getModelAttributes,
  };
}

export type ObjectAttributesEditorState = ReturnType<typeof useObjectAttributes>;
